import React, { createContext, useContext, useState, useCallback, useRef } from 'react'

/**
 * Notification context for managing floating notifications across the application
 * Prevents duplicate error messages per page load and provides centralized error handling
 */

const NotificationContext = createContext()

export const useNotification = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider')
  }
  return context
}

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([])
  const [errorShown, setErrorShown] = useState(false)
  const [errorMessages, setErrorMessages] = useState(new Set())
  const errorTimeoutRef = useRef(null)

  /**
   * Add a new notification
   */
  const addNotification = useCallback((notification) => {
    const id = Date.now() + Math.random()
    const newNotification = {
      id,
      timestamp: Date.now(),
      ...notification
    }

    setNotifications(prev => [...prev, newNotification])

    // Auto-remove success/info/warning notifications after 30 seconds
    // Error notifications are persistent and must be manually closed
    if (notification.type !== 'error') {
      setTimeout(() => {
        removeNotification(id)
      }, 5000)
    }

    return id
  }, [])

  /**
   * Remove a specific notification
   */
  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id))
  }, [])

  /**
   * Clear all notifications
   */
  const clearNotifications = useCallback(() => {
    setNotifications([])
  }, [])

  /**
   * Show error notification (only once per page load per unique message)
   */
  const showError = useCallback((message, title = 'Error') => {
    // Create a unique key for this error message
    const errorKey = `${title}:${message}`
    
    // If this exact error message already shown this page load, don't show another
    if (errorMessages.has(errorKey)) {
      return null
    }

    // Mark this error message as shown
    setErrorMessages(prev => new Set([...prev, errorKey]))

    // Add error notification
    const notificationId = addNotification({
      type: 'error',
      title,
      message,
      persistent: true // Error notifications don't auto-remove
    })

    // Clear error shown flag after 10 seconds to allow new errors
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current)
    }
    
    errorTimeoutRef.current = setTimeout(() => {
      setErrorMessages(new Set()) // Reset all error messages
    }, 10000)

    return notificationId
  }, [errorMessages, addNotification])

  /**
   * Show success notification
   */
  const showSuccess = useCallback((message, title = 'Success') => {
    return addNotification({
      type: 'success',
      title,
      message
    })
  }, [addNotification])

  /**
   * Show info notification
   */
  const showInfo = useCallback((message, title = 'Info') => {
    return addNotification({
      type: 'info',
      title,
      message
    })
  }, [addNotification])

  /**
   * Show warning notification
   */
  const showWarning = useCallback((message, title = 'Warning') => {
    return addNotification({
      type: 'warning',
      title,
      message
    })
  }, [addNotification])

  /**
   * Reset error shown state (useful for testing or manual reset)
   */
  const resetErrorState = useCallback(() => {
    setErrorShown(false)
    setErrorMessages(new Set())
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current)
      errorTimeoutRef.current = null
    }
  }, [])

  const value = {
    notifications,
    addNotification,
    removeNotification,
    clearNotifications,
    showError,
    showSuccess,
    showInfo,
    showWarning,
    resetErrorState,
    errorShown
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}
